import { Button, Divider, InputRef, Modal, Space, Tooltip } from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import { Form, Input, Select } from "antd";
import { UserProps } from "../../interfaces/interfaces";
import { USER_INITIAL_STATE } from "../../utils/data";
import DataContext from "../../context/DataContext";
import { PlusOutlined } from "@ant-design/icons";
import AuthContext from "../../context/AuthContext";
import { filterOption } from "../../utils/func/utils";

interface Props {
  modalStatus: {
    open: boolean;
    create: boolean;
    update: boolean;
    multiple: boolean;
    payload?: UserProps | null;
  };
  onCancel: () => void;
  onSubmit: (values: UserProps) => void;
}

export const ModalUserForm = ({
  modalStatus: { payload, open, create },
  onCancel,
  onSubmit,
}: Props) => {
  const [form] = Form.useForm();
  const { userGroups, handleCreateData, getData } = useContext(DataContext);
  const { clientInfo, profileInfo } = useContext(AuthContext);
  const inputRef = useRef<InputRef>(null);
  const [groupName, setGroupName] = useState("");
  const [values, setValues] = useState<UserProps>(USER_INITIAL_STATE);
  const onFinish = () => {
    onSubmit({
      name: values.name,
      username: values.username,
      groupList: values.groupList,
      role: values.role,
    } as UserProps);
  };

  useEffect(() => {
    if (values) {
      form.setFieldsValue(values);
    } else {
      form.setFieldsValue(USER_INITIAL_STATE);
      setValues(USER_INITIAL_STATE);
    }
  }, [values]);

  useEffect(() => {
    if (payload) {
      setValues(payload);
    } else {
      setValues(USER_INITIAL_STATE);
    }
  }, [payload]);

  return (
    <Modal
      onOk={onCancel}
      onCancel={onCancel}
      footer={null}
      open={open}
      title="User"
    >
      <Form
        form={form}
        initialValues={values}
        layout="vertical"
        onFinish={onFinish}
      >
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: "Please input the name" }]}
        >
          <Input
            value={values?.name}
            onChange={(event) =>
              setValues((prev) => {
                return {
                  ...prev,
                  name: event.target.value,
                };
              })
            }
          />
        </Form.Item>
        {create && (
          <>
            <Form.Item
              name="username"
              label="Email"
              rules={[
                { required: true, message: "Please input the email" },
                { type: "email", message: "Please input a valid email" },
              ]}
            >
              <Input
                value={values?.username}
                onChange={(event) =>
                  setValues((prev) => {
                    return {
                      ...prev,
                      username: event.target.value,
                    };
                  })
                }
              />
            </Form.Item>
            <Form.Item
              name="username2"
              label="Confirm Email"
              className="collections-form__form-item"
              rules={[
                {
                  required: true,
                  message: "Please confirm the email",
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("username") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("The  email that you entered do not match!"),
                    );
                  },
                }),
              ]}
            >
              <Input
                placeholder="Confirm email Address"
                value={values?.username2}
                onChange={(event) =>
                  setValues((prev) => {
                    return {
                      ...prev,
                      username2: event.target.value,
                    };
                  })
                }
              />
            </Form.Item>
          </>
        )}
        <Form.Item
          label="Role"
          rules={[{ required: true, message: "Please select a Role" }]}
        >
          <Select
            filterOption={filterOption}
            showSearch
            value={values.role.permissions}
            disabled={values.username === profileInfo?.username}
            onChange={(value) => {
              setValues({
                ...values,
                role: { permissions: value, description: value },
              });
            }}
            options={[
              { label: "Admin", value: "ADMIN" },
              { label: "Employee", value: "USER" },
              { label: "Client", value: "CLIENT" },
            ]}
            placeholder="Select Role"
          />
        </Form.Item>

        <Form.Item label="Groups">
          <Select
            mode="multiple"
            placeholder="Select Group"
            options={userGroups?.map((group) => ({
              label: group.name,
              value: group._id,
            }))}
            filterOption={filterOption}
            showSearch
            value={values.groupList?.map((group) => group._id)}
            onChange={(value) => {
              const selected = userGroups.filter((user) =>
                value.includes(user._id as any),
              );

              setValues((prev) => ({ ...prev, groupList: selected }));
            }}
            dropdownRender={(menu) => (
              <>
                {menu}
                <Divider style={{ margin: "8px 0" }} />
                <Space style={{ padding: "0 8px 4px" }}>
                  <Input
                    placeholder="New Group"
                    ref={inputRef}
                    value={groupName}
                    onChange={(event) => setGroupName(event.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                  />
                  <Tooltip title="Add New Group">
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        clientInfo?._id &&
                          handleCreateData({
                            endpoint: "groups",
                            idClient: clientInfo?._id,
                            data: { name: groupName },
                            next: () => {
                              getData("groups", clientInfo?._id);
                              setGroupName("");
                              inputRef.current?.blur();
                            },
                            showAlert: false,
                          });
                      }}
                    ></Button>
                  </Tooltip>
                </Space>
              </>
            )}
          ></Select>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit">
            Save
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};
